import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useLeaveAccrual } from "../../lib/api";
import { SectionCard, Stat } from "../labour/shared";

const VERDICT_CLS: Record<string, string> = {
  level: "bg-green-50 text-status-green",
  over: "bg-amber-50 text-status-amber",
  under: "bg-blue-50 text-brand-primary",
};

/** What each person holds against what they have earned (HRX-04).
 *
 * This is the wave's finding made countable. Leave is granted in full on 1 January, so on any day but the last
 * of the year everybody is carrying days nobody has worked for — and for the fifteen people who joined during
 * the year the difference is most of their entitlement. Nothing here is written: correcting an allocation has a
 * payslip behind it and belongs to HR, not to a report that runs when somebody opens a screen. */
export default function Accrual({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useLeaveAccrual(company || undefined);

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const nf = (n: number) => n.toLocaleString(lng === "fr" ? "fr-CD" : "en-GB", { maximumFractionDigits: 1 });

  if (!d.period) {
    return <div className="mt-6 rounded border border-brand-grey-light p-6 text-center text-sm text-brand-grey">{t("lv.noPeriod")}</div>;
  }

  return (
    <div className="mt-4 space-y-4" data-leave-accrual>
      <p className="max-w-4xl text-sm text-brand-grey">
        {t(d.accrues ? "lv.accrualOnHint" : "lv.accrualOffHint",
          { type: d.leave_type, from: d.period.from_date, to: d.period.to_date })}
      </p>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-leave-accrual-strip>
        <Stat label={t("lv.unearned")} value={nf(d.summary.unearned_days)} cls={d.summary.unearned_days ? "text-status-amber" : ""}
          hint={t("lv.unearnedHint")} />
        <Stat label={t("lv.held")} value={nf(d.summary.allocated_days)} hint={t("lv.heldHint")} />
        <Stat label={t("lv.earnedSoFar")} value={nf(d.summary.earned_days)} hint={t("lv.earnedHint")} />
        <Stat label={t("lv.overAllocated")} value={d.summary.over_allocated} cls={d.summary.over_allocated ? "text-status-amber" : ""}
          hint={t("lv.ofPeople", { n: d.summary.people })} />
        <Stat label={t("lv.underAllocated")} value={d.summary.under_allocated} cls={d.summary.under_allocated ? "text-brand-primary" : ""}
          hint={t("lv.underHint")} />
      </div>

      <SectionCard title={t("lv.perPerson")} aside={`${d.leave_type} · ${d.period.from_date} → ${d.period.to_date}`}>
        <div className="max-h-[36rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr>
                <th className="px-4 py-2">{t("wfa.person")}</th>
                <th className="px-2 py-2">{t("lv.grade")}</th>
                <th className="px-2 py-2">{t("lv.joined")}</th>
                <th className="px-2 py-2 text-right">{t("lv.entitled")}</th>
                <th className="px-2 py-2 text-right">{t("lv.earnedSoFar")}</th>
                <th className="px-2 py-2 text-right">{t("lv.held")}</th>
                <th className="px-2 py-2 text-right">{t("lv.gap")}</th>
                <th className="px-4 py-2">{t("ops.status")}</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.name} className="border-t border-brand-grey-light" data-leave-person={r.name}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.employee_name}</div>
                    <div className="text-[11px] text-brand-grey">{r.designation}{r.company ? ` · ${r.company}` : ""}</div>
                  </td>
                  <td className="px-2 py-2 text-[11px]">
                    {r.entitlement.graded
                      ? r.grade
                      : <span className="text-status-amber">{t("lv.ungraded")}</span>}
                  </td>
                  <td className="px-2 py-2 tabular-nums text-brand-grey">{r.date_of_joining}</td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {r.entitlement.annual}
                    {r.entitlement.seniority ? <span className="ml-1 text-[10px] text-brand-grey">+{r.entitlement.seniority}</span> : null}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">{r.earned}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-semibold">{r.allocated}</td>
                  <td className={`px-2 py-2 text-right font-semibold tabular-nums ${r.gap > 0 ? "text-status-amber" : r.gap < 0 ? "text-brand-primary" : ""}`}>
                    {r.gap > 0 ? "+" : ""}{r.gap}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${VERDICT_CLS[r.verdict] ?? ""}`}>
                      {t(`lv.verdict.${r.verdict}`)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
